/**
 * КАРТКА КОНТРАГЕНТА — повноцінна сторінка (рішення власника 2026-08-14:
 * «карточки — обычными страницами, а не огрызками»; раніше — Drawer
 * поверх списку). Маршрути: /contractors/new і /contractors/:id.
 *
 * Склад (ч.742, рішення власника): три вкладки картки — «загальне»
 * (реквізити, Норма #20 live-валідація) / «контакти» (телефон, email, адреси,
 * контактні особи, адреси постачання) / «банкрахунки» (IBAN + назва банку з
 * довідника за МФО; основний рахунок іде у платіжки та реєстр у банк) +
 * «договори» для збереженого контрагента. Для НОВОГО контрагента банк-рахунки
 * накопичуються локально і створюються одразу після збереження картки.
 * Поле КВЕД з форми прибрано (лишається в API/БД для міграції).
 */
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ContractorCodeHint from '@/shared/components/ContractorCodeHint';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import {
  contractors, orgs, contractorNested,
  type ContractorCreate, type ContractorType,
  type ContractorTaxSystem, type ContractRow, type ContractorContact,
} from '@/api/catalogs';
import { refs } from '@/api/refs';
import { projects } from '@/api/projects';
import {
  ContractsTab, ContactsTab, BankTab, DeliveryAddressesTab, type BankAccountDraft,
} from '@/pages/ContractorRelatedTabs';
import { quarantine } from '@/api/quarantine';
import { api } from '@/api/client';
import ImportedBadge from '@/shared/components/ImportedBadge';
import { useDefaults } from '@/store/defaults';
import { useConfirm } from '@/components/ConfirmProvider';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import {
  DraftSlotsProvider, confirmPendingRows, type DraftSlots,
} from '@/shared/hooks/draftSlots';
import { signalUserEdit } from '@/store/formDrafts';
import {
  CONTRACTOR_OPFG_CODES, FOP_OPFG_CODE, INDIVIDUAL_OPFG_CODE, compileOrgNames, findBankByIban,
  findOpfgByKey, opfgKey, opfgKeyFor,
  type BankRow,
} from '@/shared/ukr_classifiers';
import SearchSelect from '@/components/SearchSelect';
import { invalidateRefLists } from '@/shared/utils/refLists';
import { BankAccountDuplicates } from '@/shared/components/BankAccountDuplicates';

const EMPTY: ContractorCreate = {
  code: null, name: '', full_name: null,
  contractor_type: 'LEGAL',
  edrpou: null, inn: null,
  is_vat_payer: false, vat_number: null, tax_system: null,
  legal_address: null, actual_address: null,
  phone: null, email: null,
  is_buyer: false, is_supplier: false, is_other: false,
  is_non_resident: false, group_id: null, kved_code: null,
  opfg_code: null, opfg_name: null, own_name: null, opf_short: null, reg_number: null,
};

// Норма #20 v2: лише формат (рішення власника 2026-06-02 — без checksum).
const isValidEdrpou = (v: string) => /^\d{8}$/.test(v);
const isValidRnokpp = (v: string) => /^\d{10}$/.test(v);
const isValidVatNumber = (v: string) => /^\d{9,12}$/.test(v);

const TAX_SYSTEMS_BY_TYPE: Record<ContractorType, ContractorTaxSystem[]> = {
  LEGAL: ['GENERAL', 'SIMPLIFIED_3', 'SIMPLIFIED_4'],
  INDIVIDUAL_FOP: ['FOP_GENERAL', 'FOP_1', 'FOP_2', 'FOP_3_3', 'FOP_3_5'],
  INDIVIDUAL: [],
  SEPARATE_DIVISION: [],
};

const TAX_SYSTEM_LABELS: Record<ContractorTaxSystem, string> = {
  GENERAL: 'Загальна (податок на прибуток)',
  SIMPLIFIED_3: 'Спрощена 3 група (5% / 3% з ПДВ)',
  SIMPLIFIED_4: 'Спрощена 4 група (с/г)',
  FOP_GENERAL: 'ФОП на загальній (ст. 177 ПКУ)',
  FOP_1: 'ФОП 1 група (єдиний, без ПДВ)',
  FOP_2: 'ФОП 2 група (єдиний, без ПДВ)',
  FOP_3_3: 'ФОП 3 група 3% (з ПДВ)',
  FOP_3_5: 'ФОП 3 група 5% (без ПДВ)',
};

function vatPayerFixedByTaxSystem(ts: ContractorTaxSystem | null): boolean | null {
  if (ts === 'FOP_1' || ts === 'FOP_2' || ts === 'FOP_3_5') return false;
  if (ts === 'FOP_3_3') return true;
  return null;
}

type CardTab = 'general' | 'contacts' | 'bank' | 'contracts';

interface PendingBank extends BankAccountDraft { id: string }

export default function ContractorCardPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [form, setForm] = useState<ContractorCreate>(EMPTY);
  const [loadedId, setLoadedId] = useState<string | null>(null);
  const [tab, setTab] = useState<CardTab>('general');
  // Новий контрагент: рахунки до збереження живуть локально.
  const [pendingBanks, setPendingBanks] = useState<PendingBank[]>([]);

  const cQ = useQuery({
    queryKey: ['contractor', id],
    queryFn: () => contractors.get(id!),
    enabled: !isNew,
  });
  const c = cQ.data;
  // Заповнити форму ПІСЛЯ завантаження (раз на id).
  if (c && loadedId !== c.id) {
    setLoadedId(c.id);
    setForm({
      code: c.code, name: c.name, full_name: c.full_name,
      contractor_type: c.contractor_type,
      edrpou: c.edrpou, inn: c.inn,
      is_vat_payer: c.is_vat_payer, vat_number: c.vat_number,
      tax_system: c.tax_system,
      legal_address: c.legal_address, actual_address: c.actual_address,
      phone: c.phone, email: c.email,
      is_buyer: c.is_buyer, is_supplier: c.is_supplier, is_other: c.is_other,
      is_non_resident: c.is_non_resident, group_id: c.group_id,
      kved_code: c.kved_code,
      opfg_code: c.opfg_code ?? null, opfg_name: c.opfg_name ?? null,
      // legacy/імпорт без own_name: «як є» — Назва = повна (або коротка), Скорочено = коротка
      own_name: c.own_name ?? (c.full_name || c.name),
      opf_short: c.opf_short ?? c.name,
      reg_number: c.reg_number ?? null,
    });
  }

  // ч.1059: чернетка незбережених правок (як у документах). Для НОВОГО
  // контрагента разом з реквізитами бережемо й банк-рахунки, що ще не
  // збережені. Контакти/адреси/рахунки збереженого контрагента мають власні
  // мутації — картку вони не зберігають, тому лише явний markSaved.
  // ч.1060: + слоти вкладок (рядки «новий контакт / адреса / рахунок», набрані
  // до кнопки «додати») — не губляться при перемиканні вкладок і в чернетці.
  const [slots, setSlots] = useState<DraftSlots>({});
  const draftValue = useMemo(() => ({ form, pendingBanks, slots }), [form, pendingBanks, slots]);
  const restoreDraft = useCallback((v: {
    form: ContractorCreate; pendingBanks?: PendingBank[]; slots?: DraftSlots;
  }) => {
    setForm({ ...EMPTY, ...v.form });
    setPendingBanks(v.pendingBanks ?? []);
    setSlots(v.slots ?? {});
  }, []);
  const draft = useFormDraft(draftValue, restoreDraft, {
    ready: isNew || (!!c && loadedId === c.id),
    subject: 'цієї картки',
    clearOnAnyMutation: false,
  });

  const editingId = isNew ? null : (id ?? null);
  const groupsQ = useQuery({ queryKey: ['contractor-groups'], queryFn: refs.contractorGroups.list });
  // ч.829 (зауваження власника): договори лише організації за замовчуванням
  // з шапки; «усі організації» у шапці — усі договори контрагента.
  const defaultOrgId = useDefaults((s) => s.defaultOrgId);
  const contractsQ = useQuery({
    queryKey: ['contractor-contracts', editingId, defaultOrgId],
    queryFn: () =>
      api.get('/refs/contracts', { params: { contractor_id: editingId, organization_id: defaultOrgId || undefined } })
        .then((r) => r.data as ContractRow[]),
    enabled: editingId !== null,
  });
  const civilKindsQ = useQuery({ queryKey: ['contract-civil-kinds'], queryFn: refs.contractCivilKinds.list });
  const orgsListQ = useQuery({ queryKey: ['orgs-for-contract'], queryFn: () => orgs.list({ limit: 200 }) });
  const currenciesQ = useQuery({ queryKey: ['currencies'], queryFn: refs.currencies });
  const projectsForContractQ = useQuery({
    queryKey: ['projects-for-contract'],
    queryFn: () => projects.list({ limit: 200 }),
    enabled: editingId !== null,
  });
  const bankAccountsQ = useQuery({
    queryKey: ['contractor-bank-accounts', editingId],
    queryFn: () => refs.contractorBankAccounts(editingId || undefined),
    enabled: editingId !== null,
  });
  const deliveryAddressesQ = useQuery({
    queryKey: ['contractor-delivery-addresses', editingId],
    queryFn: () => contractorNested.listDeliveryAddresses(editingId!),
    enabled: editingId !== null,
  });
  const contactsQ = useQuery({
    queryKey: ['contractor-contacts', editingId],
    queryFn: () =>
      api.get('/refs/contractor-contacts', { params: { contractor_id: editingId } })
        .then((r) => r.data as ContractorContact[]),
    enabled: editingId !== null,
  });
  const banksQ = useQuery({
    queryKey: ['banks'],
    queryFn: () => refs.banks() as Promise<BankRow[]>,
    staleTime: 1000 * 60 * 60,
  });
  const quarantineQ = useQuery({
    queryKey: ['contractor-quarantine', editingId],
    queryFn: () => quarantine.contractors({ include_resolved: false, limit: 500 }),
    enabled: editingId !== null && !!c?.needs_review,
  });
  const quarantineIssues = useMemo(() => {
    if (!editingId || !quarantineQ.data) return [];
    const rec = quarantineQ.data.items.find((q) => q.entity_id === editingId);
    return rec?.issues ?? [];
  }, [editingId, quarantineQ.data]);

  const afterSave = () => {
    draft.markSaved();
    qc.invalidateQueries({ queryKey: ['contractor', id] });
    qc.invalidateQueries({ queryKey: ['quarantine'] });
    invalidateRefLists(qc, 'contractors');   // ч.1267: списки у відкритих документах
    navigate('/contractors');
  };
  const create = useMutation({
    mutationFn: async (p: ContractorCreate) => {
      const created = await contractors.create(p);
      // Локально накопичені банк-рахунки → створюємо після картки.
      for (const b of pendingBanks) {
        await contractorNested.createBank({
          contractor_id: created.id, iban: b.iban, currency_id: b.currency_id,
          account_name: b.account_name, is_main: b.is_main,
        });
      }
      return created;
    },
    onSuccess: afterSave,
  });
  const update = useMutation({
    mutationFn: (p: Partial<ContractorCreate>) => contractors.update(id!, p),
    onSuccess: afterSave,
  });
  const remove = useMutation({
    mutationFn: () => contractors.remove(id!),
    onSuccess: () => { draft.markSaved(); navigate('/contractors'); },
  });

  const save = async () => {
    // ч.1060: недодані рядки (контакт, адреса, рахунок) — питаємо, щоб не
    // загубились мовчки: після збереження картка закривається.
    if (!(await confirmPendingRows(slots, confirm))) return;
    if (isNew) create.mutate(form);
    else update.mutate(form);
  };

  // ===== Банк-рахунки: збережений контрагент — API, новий — локальний список =====
  const invalidateBanks = () => qc.invalidateQueries({ queryKey: ['contractor-bank-accounts', editingId] });
  const uahId = (currenciesQ.data ?? []).find((cc) => cc.code === 'UAH')?.id ?? '';
  const bankRows = isNew
    ? pendingBanks.map((b) => ({ ...b, owner_id: '' }))
    : (bankAccountsQ.data ?? []).map((ba) => ({
        id: ba.id, owner_id: c?.id ?? '', iban: ba.iban,
        currency_id: ba.currency_id || uahId, account_name: ba.account_name ?? null, is_main: ba.is_main,
      }));
  const addBank = async (d: BankAccountDraft) => {
    if (isNew) {
      signalUserEdit();
      setPendingBanks((prev) => [...prev, { ...d, id: `tmp-${Date.now()}` }]);
      return;
    }
    await contractorNested.createBank({
      contractor_id: c!.id, iban: d.iban, currency_id: d.currency_id,
      account_name: d.account_name, is_main: d.is_main,
    });
    await invalidateBanks();
  };
  const removeBank = async (bid: string) => {
    if (isNew) {
      signalUserEdit();
      setPendingBanks((prev) => {
        const rest = prev.filter((b) => b.id !== bid);
        // якщо видалили основний — першим стає основним
        return rest.some((b) => b.is_main) || rest.length === 0
          ? rest : rest.map((b, i) => ({ ...b, is_main: i === 0 }));
      });
      return;
    }
    await contractorNested.removeBank(bid);
    await invalidateBanks();
  };
  const setMainBank = async (bid: string) => {
    if (isNew) {
      signalUserEdit();
      setPendingBanks((prev) => prev.map((b) => ({ ...b, is_main: b.id === bid })));
      return;
    }
    const cur = (bankAccountsQ.data ?? []).filter((b) => b.is_main && b.id !== bid);
    for (const b of cur) await contractorNested.updateBank(b.id, { is_main: false });
    await contractorNested.updateBank(bid, { is_main: true });
    await invalidateBanks();
  };

  // ===== ч.747: оргформа → тип контрагента; назви компілюються =====
  const withNames = (f: ContractorCreate): ContractorCreate => {
    const c = compileOrgNames({
      opfg_code: f.opfg_code ?? null, opfg_name: f.opfg_name ?? null,
      own_name: f.own_name ?? null, opf_short: f.opf_short ?? null,
    });
    return c ? { ...f, name: c.name, full_name: c.full_name } : f;
  };
  const typeByOpfg = (code: string | null): ContractorType => {
    if (code === INDIVIDUAL_OPFG_CODE) return 'INDIVIDUAL';
    if (code === FOP_OPFG_CODE) return 'INDIVIDUAL_FOP';
    // ч.1195: коди за ДК 002:2004 — 610 філія, 620 представництво
    if (code === '610' || code === '620') return 'SEPARATE_DIVISION';
    return 'LEGAL';
  };
  const applyOpfg = (key: string | null) => {
    const opt = findOpfgByKey(key);
    const code = opt?.code ?? null;
    const ct = typeByOpfg(code);
    const opfgName = opt?.name ?? null;
    setForm(withNames({
      ...form, opfg_code: code, opfg_name: opfgName, contractor_type: ct,
      tax_system: ct === form.contractor_type ? form.tax_system : null,
      is_vat_payer: ct === 'INDIVIDUAL' ? false : form.is_vat_payer,
      edrpou: ct === 'LEGAL' || ct === 'SEPARATE_DIVISION' ? form.edrpou : null,
      inn: ct === 'INDIVIDUAL' || ct === 'INDIVIDUAL_FOP' ? form.inn : null,
    }));
  };
  const setResidency = (nonResident: boolean) => {
    setForm(withNames({
      ...form, is_non_resident: nonResident,
      // нерезидент: оргформа неактивна, укр. коди не застосовні
      opfg_code: nonResident ? null : form.opfg_code,
      opfg_name: nonResident ? null : form.opfg_name,
      contractor_type: nonResident ? 'LEGAL' : form.contractor_type,
      tax_system: nonResident ? null : form.tax_system,
      edrpou: nonResident ? null : form.edrpou,
      inn: nonResident ? null : form.inn,
      is_vat_payer: nonResident ? false : form.is_vat_payer,
      vat_number: nonResident ? null : form.vat_number,
    }));
  };

  // ===== Live validation =====
  const edrpouValid = form.edrpou ? isValidEdrpou(form.edrpou) : null;
  const innValid = form.inn ? isValidRnokpp(form.inn) : null;
  const vatNumValid = form.vat_number ? isValidVatNumber(form.vat_number) : null;

  const nonRes = form.is_non_resident;
  const isFop = form.contractor_type === 'INDIVIDUAL_FOP';
  const isLegal = form.contractor_type === 'LEGAL';
  const isIndividual = form.contractor_type === 'INDIVIDUAL';
  const isSeparateDivision = form.contractor_type === 'SEPARATE_DIVISION';
  const showEdrpou = !nonRes && (isLegal || isSeparateDivision);
  const showRnokpp = !nonRes && (isFop || isIndividual);
  const availableTaxSystems = TAX_SYSTEMS_BY_TYPE[form.contractor_type];
  const showTaxSystem = !nonRes && availableTaxSystems.length > 0;
  const showVatBlock = !nonRes && !isIndividual;
  const vatFixed = vatPayerFixedByTaxSystem(form.tax_system ?? null);
  const ownName = (form.own_name ?? '').trim();

  // Обовʼязкові поля позначено *; списку підказок знизу немає (рішення власника).
  const formErrors: string[] = [];
  if (!ownName) formErrors.push('name');
  if (!nonRes && !form.opfg_code) formErrors.push('opfg');
  if (showEdrpou && isLegal && (!form.edrpou || !edrpouValid)) formErrors.push('edrpou');
  if (showEdrpou && form.edrpou && !edrpouValid) formErrors.push('edrpou');
  if (showRnokpp && isFop && (!form.inn || !innValid)) formErrors.push('inn');
  if (showRnokpp && form.inn && !innValid) formErrors.push('inn');
  if (form.vat_number && !vatNumValid) formErrors.push('vat');
  if (form.is_vat_payer && !form.vat_number) formErrors.push('vat');
  if (!form.is_buyer && !form.is_supplier && !form.is_other) formErrors.push('role');
  const canSave = formErrors.length === 0;

  if (!isNew && cQ.isLoading) {
    return <div className="card"><p>Завантаження…</p></div>;
  }
  if (!isNew && cQ.isError) {
    return (
      <div className="card">
        <p>Контрагента не знайдено.</p>
        <button onClick={() => navigate('/contractors')}>до списку</button>
      </div>
    );
  }

  const actualSameAsLegal = form.actual_address === null || form.actual_address === form.legal_address;
  const bankCount = isNew ? pendingBanks.length : (bankAccountsQ.data?.length ?? 0);
  const contactsCount = (contactsQ.data?.length ?? 0) + (deliveryAddressesQ.data?.length ?? 0);

  return (
    <DraftSlotsProvider slots={slots} setSlots={setSlots}>
    <div className="card">
      <div className="toolbar" style={{ flexWrap: 'wrap', gap: '0.4rem' }}>
        <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          {isNew ? t('contractor.new') : (c?.name ?? '')}
          {c && <ImportedBadge source={c.external_source} />}
        </h2>
        {/* ч.642: канон DocumentToolbar — плашка дій + інфо-ряд у рядку назви. */}
        <DocumentToolbar
          onBackToList={() => navigate('/contractors')}
          onSave={save}
          saveDisabled={!canSave || create.isPending || update.isPending}
          savePending={create.isPending || update.isPending}
          postable={false}
          isNew={isNew}
          ownerDoc={c ? { type: 'ref.contractors', id: c.id } : undefined}
          onDelete={() => { confirm(t('common.confirm_delete')).then((ok) => { if (ok) remove.mutate(); }); }}
          extraStatusBadges={c?.needs_review
            ? [{ label: 'потребує перевірки', tone: 'amber',
                 title: 'Картку взято у карантин міграції — виправте реквізити' }] : []}
        />
      </div>

      {/* ч.1094: картку обʼєднано з іншою (дубль) — працюємо з основною. */}
      {c?.merged_into_id && (
        <div style={{
          background: 'var(--badge-amber-bg)', border: '1px solid var(--badge-amber-bd)',
          borderRadius: 6, padding: '0.75rem 1rem', marginBottom: '1rem', color: 'var(--badge-amber-fg)',
        }}>
          Цю картку обʼєднано з іншою як дубль — її документи перенесено на основну картку.{' '}
          <a href={`/contractors/${c.merged_into_id}`}
            onClick={(e) => { e.preventDefault(); navigate(`/contractors/${c.merged_into_id}`); }}>
            відкрити основну картку
          </a>
        </div>
      )}

      {/* Migration Quarantine: банер з переліком що виправити. */}
      {c?.needs_review && (
        <div style={{
          background: 'var(--badge-amber-bg)', border: '1px solid var(--badge-amber-bd)',
          borderRadius: 6, padding: '0.75rem 1rem', marginBottom: '1rem',
        }}>
          <div style={{ fontWeight: 600, color: 'var(--badge-amber-fg)', marginBottom: 6 }}>
            ⚠ Контрагент у карантині міграції
          </div>
          <div style={{ fontSize: 13, color: 'var(--badge-amber-fg)', marginBottom: 8 }}>
            Реквізити з 1С не відповідають стандарту. Виправте поля нижче —
            після збереження контрагент стане доступним у документах.
          </div>
          {quarantineIssues.length > 0 && (
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--badge-amber-fg)' }}>
              {quarantineIssues.map((iss, idx) => (
                <li key={idx}>{iss.message_uk}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* ч.742: три вкладки картки (+ договори для збереженого) — канон seg-tabs. */}
      <div className="seg-tabs">
        <button type="button" className={tab === 'general' ? 'active' : ''}
          onClick={() => setTab('general')}>
          Загальне
        </button>
        <button type="button" className={tab === 'contacts' ? 'active' : ''}
          onClick={() => setTab('contacts')}>
          Контакти{!isNew && contactsCount > 0 ? ` (${contactsCount})` : ''}
        </button>
        <button type="button" className={tab === 'bank' ? 'active' : ''}
          onClick={() => setTab('bank')}>
          Банкрахунки{bankCount > 0 ? ` (${bankCount})` : ''}
        </button>
        {!isNew && (
          <button type="button" className={tab === 'contracts' ? 'active' : ''}
            onClick={() => setTab('contracts')}>
            Договори ({contractsQ.data?.length ?? 0})
          </button>
        )}
      </div>

      {tab === 'general' && (
        <div style={{ maxWidth: 1200 }}>
          {/* Рядок 1: Резидент/Нерезидент | Оргформа | Назва (без оргформи) | Скорочено | Код. */}
          <div className="doc-grid" style={{ alignItems: 'flex-start' }}>
            <div className="field" style={{ gridColumn: 'span 2' }}>
              <label>Резидентність</label>
              <select value={nonRes ? 'NR' : 'R'} onChange={(e) => setResidency(e.target.value === 'NR')}>
                <option value="R">Резидент</option>
                <option value="NR">Нерезидент</option>
              </select>
            </div>
            <div className="field" style={{ gridColumn: 'span 2' }}>
              <label>Оргформа{!nonRes && ' *'}</label>
              <SearchSelect
                items={CONTRACTOR_OPFG_CODES.map((o) => ({ id: opfgKey(o), label: o.name, hint: o.code === INDIVIDUAL_OPFG_CODE ? null : o.code }))}
                value={opfgKeyFor(form.opfg_code, form.opfg_name)}
                placeholder={nonRes ? '—' : 'фізособа, ТОВ, ФОП…'}
                disabled={nonRes}
                onChange={(id) => applyOpfg(id || null)}
              />
            </div>
            <div className="field" style={{ gridColumn: 'span 4' }}>
              <label>{isIndividual || isFop ? 'ПІБ *' : 'Назва (без оргформи) *'}</label>
              <input
                value={form.own_name ?? ''}
                disabled={!nonRes && !form.opfg_code}
                placeholder={!nonRes && !form.opfg_code ? 'спершу оберіть оргформу' : ''}
                onChange={(e) => setForm(withNames({ ...form, own_name: e.target.value || null }))}
              />
            </div>
            <div className="field" style={{ gridColumn: 'span 2' }}>
              <label>Скорочено</label>
              <input
                value={form.opf_short ?? ''}
                disabled={!nonRes && !form.opfg_code}
                maxLength={255}
                placeholder={ownName ? form.name : ''}
                onChange={(e) => setForm(withNames({ ...form, opf_short: e.target.value || null }))}
              />
            </div>
            <div className="field" style={{ gridColumn: 'span 2' }}>
              <label>{t('contractor.code')}</label>
              <input value={form.code ?? ''}
                onChange={(e) => setForm({ ...form, code: e.target.value || null })}
                placeholder="CTR-1 (auto)" />
            </div>
          </div>
          {ownName && (
            <div style={{ fontSize: '0.82rem', color: 'var(--muted)', margin: '-0.25rem 0 0.5rem' }}>
              {form.name}<span style={{ margin: '0 0.5rem' }}>·</span>{form.full_name}
            </div>
          )}

          {/* ч.1095: контрагент з таким кодом уже є — попередження над полем коду. */}
          {!nonRes && (
            <ContractorCodeHint
              code={showRnokpp ? form.inn : form.edrpou}
              /* ч.1237: у новій картці — і за назвою (наявна картка могла бути без коду) */
              name={editingId ? null : form.name}
              excludeId={editingId}
              onOpen={(m) => navigate(`/contractors/${m.id}`)} />
          )}

          {/* Рядок 2: ЄДРПОУ / РНОКПП / Регномер (нерезидент) | Роль | Група. */}
          <div className="doc-grid" style={{ alignItems: 'flex-start' }}>
            {nonRes ? (
              <div className="field" style={{ gridColumn: 'span 2' }}>
                <label>Реєстраційний номер</label>
                <input value={form.reg_number ?? ''} maxLength={50}
                  onChange={(e) => setForm({ ...form, reg_number: e.target.value || null })} />
              </div>
            ) : showRnokpp ? (
              <div className="field" style={{ gridColumn: 'span 2' }}>
                <label>
                  РНОКПП{isFop && ' *'}
                  {form.inn && (
                    <span style={{ marginLeft: 6, color: innValid ? 'green' : 'red', fontWeight: 'bold' }}>
                      {innValid ? '✓' : '✗'}
                    </span>
                  )}
                </label>
                <input value={form.inn ?? ''} maxLength={10} placeholder="10 цифр"
                  onChange={(e) => setForm({ ...form, inn: e.target.value || null })}
                  style={form.inn && !innValid ? { borderColor: 'red' } : undefined} />
              </div>
            ) : (
              <div className="field" style={{ gridColumn: 'span 2' }}>
                <label>
                  {t('contractor.edrpou')}{isLegal && ' *'}
                  {form.edrpou && (
                    <span style={{ marginLeft: 6, color: edrpouValid ? 'green' : 'red', fontWeight: 'bold' }}>
                      {edrpouValid ? '✓' : '✗'}
                    </span>
                  )}
                </label>
                <input value={form.edrpou ?? ''} maxLength={8} placeholder="8 цифр"
                  onChange={(e) => setForm({ ...form, edrpou: e.target.value || null })}
                  style={form.edrpou && !edrpouValid ? { borderColor: 'red' } : undefined} />
              </div>
            )}
            <div className="field" style={{ gridColumn: 'span 3' }}>
              <label>Роль *</label>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', minHeight: 32, alignItems: 'center' }}>
                {([['is_buyer', 'Покупець'], ['is_supplier', 'Постачальник'],
                   ['is_other', 'Iнше']] as const).map(([k, label]) => (
                  <label key={k} style={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                    <input type="checkbox" checked={form[k]}
                      onChange={(e) => setForm({ ...form, [k]: e.target.checked })}
                      style={{ width: 'auto', marginRight: '0.4rem' }} />
                    {label}
                  </label>
                ))}
              </div>
            </div>
            <div className="field" style={{ gridColumn: 'span 3' }}>
              <label>Група</label>
              <select value={form.group_id ?? ''}
                onChange={(e) => setForm({ ...form, group_id: e.target.value || null })}>
                <option value="">— без групи —</option>
                {(groupsQ.data ?? []).map((g) => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Рядок 3: Система оподаткування | ПДВ | номер свідоцтва. */}
          {(showTaxSystem || showVatBlock) && (
            <div className="doc-grid" style={{ alignItems: 'flex-start' }}>
              {showTaxSystem && (
                <div className="field" style={{ gridColumn: 'span 5' }}>
                  <label>Система оподаткування</label>
                  <select value={form.tax_system ?? ''}
                    onChange={(e) => {
                      const ts = (e.target.value || null) as ContractorTaxSystem | null;
                      const fixed = vatPayerFixedByTaxSystem(ts);
                      setForm({
                        ...form, tax_system: ts,
                        is_vat_payer: fixed !== null ? fixed : form.is_vat_payer,
                      });
                    }}>
                    <option value="">— Не вказано —</option>
                    {availableTaxSystems.map((ts) => (
                      <option key={ts} value={ts}>{TAX_SYSTEM_LABELS[ts]}</option>
                    ))}
                  </select>
                </div>
              )}
              {showVatBlock && (
                <div className="field" style={{ gridColumn: 'span 2' }}>
                  <label>&nbsp;</label>
                  <label style={{ display: 'inline-flex', alignItems: 'center', height: 34, whiteSpace: 'nowrap', margin: 0 }}
                    title={vatFixed !== null ? 'Фіксовано системою оподаткування' : undefined}>
                    <input type="checkbox" checked={form.is_vat_payer}
                      disabled={vatFixed !== null}
                      onChange={(e) => setForm({ ...form, is_vat_payer: e.target.checked })}
                      style={{ width: 'auto', marginRight: '0.5rem' }} />
                    {t('contractor.is_vat_payer')}
                  </label>
                </div>
              )}
              {showVatBlock && form.is_vat_payer && (
                <div className="field" style={{ gridColumn: 'span 5' }}>
                  <label>
                    Свідоцтво ПДВ (номер) *
                    {form.vat_number && (
                      <span style={{ marginLeft: 6, color: vatNumValid ? 'green' : 'red', fontWeight: 'bold' }}>
                        {vatNumValid ? '✓' : '✗'}
                      </span>
                    )}
                  </label>
                  <input value={form.vat_number ?? ''} maxLength={12} placeholder="9-12 цифр"
                    onChange={(e) => setForm({ ...form, vat_number: e.target.value || null })}
                    style={form.vat_number && !vatNumValid ? { borderColor: 'red' } : undefined} />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {tab === 'contacts' && (
        <div style={{ maxWidth: 1100 }}>
          <div className="doc-grid">
            <div className="field" style={{ gridColumn: 'span 6' }}>
              <label>Телефон</label>
              <input value={form.phone ?? ''} placeholder="+38 (0XX) XXX-XX-XX"
                onChange={(e) => setForm({ ...form, phone: e.target.value || null })} />
            </div>
            <div className="field" style={{ gridColumn: 'span 6' }}>
              <label>Email</label>
              <input type="email" value={form.email ?? ''}
                onChange={(e) => setForm({ ...form, email: e.target.value || null })} />
            </div>
          </div>
          <div className="doc-grid" style={{ alignItems: 'end' }}>
            <div className="field" style={{ gridColumn: 'span 6' }}>
              <label>Юридична адреса</label>
              <input value={form.legal_address ?? ''}
                onChange={(e) => setForm({ ...form, legal_address: e.target.value || null })} />
            </div>
            <div className="field" style={{ gridColumn: 'span 6' }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', minHeight: 32 }}>
                <input type="checkbox"
                  checked={actualSameAsLegal}
                  onChange={(e) => setForm({
                    ...form,
                    actual_address: e.target.checked ? form.legal_address : '',
                  })}
                  style={{ width: 'auto', marginRight: '0.5rem' }} />
                Фактична адреса співпадає з юридичною
              </label>
            </div>
          </div>
          {!actualSameAsLegal && (
            <div className="field">
              <label>Фактична адреса</label>
              <input value={form.actual_address ?? ''}
                onChange={(e) => setForm({ ...form, actual_address: e.target.value || null })} />
            </div>
          )}

          <h4 style={{ margin: '1rem 0 0.25rem' }}>
            Контактні особи{!isNew ? ` (${contactsQ.data?.length ?? 0})` : ''}
          </h4>
          {isNew || !c ? (
            <p style={{ color: 'var(--muted)', fontSize: '0.85em' }}>Доступно після збереження картки.</p>
          ) : (
            <ContactsTab
              contractorId={c.id}
              contacts={(contactsQ.data ?? []) as ContractorContact[]}
              onChanged={() => qc.invalidateQueries({ queryKey: ['contractor-contacts', editingId] })}
            />
          )}

          <h4 style={{ margin: '1rem 0 0.25rem' }}>
            Адреси постачання{!isNew ? ` (${deliveryAddressesQ.data?.length ?? 0})` : ''}
          </h4>
          {isNew || !c ? (
            <p style={{ color: 'var(--muted)', fontSize: '0.85em' }}>Доступно після збереження картки.</p>
          ) : (
            <DeliveryAddressesTab
              contractorId={c.id}
              addresses={deliveryAddressesQ.data ?? []}
              onChanged={() => void deliveryAddressesQ.refetch()}
            />
          )}
        </div>
      )}

      {tab === 'bank' && !isNew && (
        <BankAccountDuplicates
          accounts={bankAccountsQ.data ?? []}
          currencies={(currenciesQ.data ?? []).map((cc) => ({ id: cc.id, code: cc.code }))}
          onMerged={invalidateBanks}
        />
      )}
      {tab === 'bank' && (
        <BankTab
          accounts={bankRows}
          currencies={(currenciesQ.data ?? []).map((cc) => ({ id: cc.id, code: cc.code }))}
          bankNameByIban={(iban) => findBankByIban(iban, banksQ.data ?? [])}
          onAdd={addBank}
          onRemove={removeBank}
          onSetMain={setMainBank}
          pending={isNew}
        />
      )}

      {tab === 'contracts' && !isNew && c && (
        <ContractsTab
          contractorId={c.id}
          contracts={(contractsQ.data ?? []) as ContractRow[]}
          orgs={(orgsListQ.data?.items ?? []).map((o) => ({ id: o.id, name: o.name, is_vat_payer: o.is_vat_payer, use_returnable_packaging: o.use_returnable_packaging }))}
          currencies={(currenciesQ.data ?? []).map((cc) => ({ id: cc.id, code: cc.code }))}
          civilKinds={(civilKindsQ.data ?? []).map((k) => ({ id: k.id, name: k.name }))}
          projects={(projectsForContractQ.data?.items ?? []).map((p) => ({ id: p.id, name: p.name }))}
          onChanged={() => qc.invalidateQueries({ queryKey: ['contractor-contracts', editingId] })}
        />
      )}

    </div>
    </DraftSlotsProvider>
  );
}
